import {
  Grid,
  InputBaseComponentProps,
  InputLabelProps,
  LinearProgress,
  makeStyles,
  TextField,
  CircularProgress
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useField, useFormikContext } from 'formik'
import debounce from 'lodash/debounce'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { apiSauceInstance } from '../../../services/api'

export type ReasonType =
  | 'create-option'
  | 'select-option'
  | 'remove-option'
  | 'blur'
  | 'clear'

interface Value {
  name: string
  id: string
  [key: string]: any
}

export interface LabelFormatValue {
  value: string
  name: string
}

interface CommonProps {
  altValue?: string
  endAdornment?: React.ReactNode
  url: string
  name: string
  label: string
  filters?: Record<string, any>
  disabled?: boolean
  placeholder?: string
  fullWidth?: boolean
  InputProps?: InputBaseComponentProps
  InputLabelProps?: InputLabelProps
  allowSearchWhenDisabled?: boolean
  isConsulting?: boolean
  limit?: number
  paramSearch: string
  handleOnChange?: (value: any, reason: ReasonType) => void
  disableInitialFetch?: boolean
}

interface LabelFormatProps extends CommonProps {
  labelFormat: (value: any) => LabelFormatValue
  labelField?: never
}

interface LabelFieldProps extends CommonProps {
  labelField: string
  labelFormat?: never
}

export type PureAsyncSearchInputProps = LabelFormatProps | LabelFieldProps

const useStyles = makeStyles(theme => ({
  input: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.palette.action.disabled
      }
    }
  }
}))

export const PureAsyncSearchInput: React.FC<PureAsyncSearchInputProps> =
  props => {
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState<Value[]>([])
    const [initialLoading, setInitialLoading] = useState(false)
    const [field, meta, helper] = useField(props.name)
    const formik = useFormikContext()
    const [loading, setLoading] = useState(false)
    const value = typeof field.value === 'string' ? null : field.value

    const classes = useStyles()

    function getError(error?: string | { name: string; value: string }) {
      if (!error) return ''
      return typeof error === 'string' ? error : error?.value
    }

    async function handleSearchOneFromApi(idFromEntity: string): Promise<void> {
      if (props.disabled && !props.allowSearchWhenDisabled) return
      setInitialLoading(true)

      try {
        const response = await apiSauceInstance.get<any>(
          `${props.url}/${idFromEntity}`,
          props.filters
        )
        const data = response.data

        if (props.labelFormat) {
          formik.setFieldValue(props.name, props.labelFormat(data))
        } else {
          formik.setFieldValue(props.name, {
            name: data[props.labelField!],
            value: data.id
          })
        }
      } catch (error) {
        console.error('Erro ao buscar entidade individual:', error)
      } finally {
        setInitialLoading(false)
      }
    }

    // Memoriza a função de busca para evitar recriações desnecessárias
    const handleSearch = useCallback(
      async (search?: string): Promise<void> => {
        setLoading(true)
        try {
          const response = await apiSauceInstance.get<any>(props.url, {
            [props.paramSearch]: search || undefined,
            limit: props.limit ?? 10,
            ...props.filters
          })

          setOptions(
            response.data?.edges?.map((currentValue: any) => {
              if (props.labelFormat) {
                return props.labelFormat(currentValue)
              } else {
                const name = currentValue.node[props.labelField!]
                return {
                  name,
                  value: currentValue.node.id
                }
              }
            }) ?? []
          )
        } catch (error) {
          console.error('Erro ao buscar listagem:', error)
        } finally {
          setLoading(false)
        }
      },
      [
        props.url,
        props.paramSearch,
        props.limit,
        props.filters,
        props.labelFormat,
        props.labelField
      ]
    )

    // Memoriza o debounce atrelado ao ciclo de vida do componente
    const debouncedSearch = useMemo(
      () => debounce((searchVal: string) => handleSearch(searchVal), 700),
      [handleSearch]
    )

    // Cancela buscas pendentes se o componente for desmontado
    useEffect(() => {
      return () => {
        debouncedSearch.cancel()
      }
    }, [debouncedSearch])

    async function handleOnChange(e: any, value: any, reason: ReasonType) {
      if (!!props.handleOnChange) props.handleOnChange(value, reason)
      return helper.setValue(value)
    }

    useEffect(() => {
      if (!open) {
        setOptions([])
      }
    }, [open])

    const filters = JSON.stringify(props.filters)

    useEffect(() => {
      // Só busca individualmente se o valor for apenas uma String (ID bruto) e a busca inicial não estiver bloqueada
      if (
        typeof field.value === 'string' &&
        field.value &&
        !props.disableInitialFetch
      ) {
        handleSearchOneFromApi(field.value)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, props.disabled, filters, props.disableInitialFetch])

    return (
      <>
        <Autocomplete
          {...props}
          open={open}
          disabled={props.disabled}
          onOpen={() => {
            setOpen(true)
            // 💡 ANTES: chamava handleSearch() aqui.
            // 💡 AGORA: Não busca nada ao abrir, espera o usuário começar a digitar.
          }}
          onClose={() => setOpen(false)}
          onChange={(e: any, value: any, reason: ReasonType) =>
            handleOnChange(e, value, reason)
          }
          onInputChange={(_e, value, reason) => {
            // 💡 SOLUÇÃO CRÍTICA: 'input' indica que o usuário digitou fisicamente.
            // Impede requisições automáticas no carregamento da página (que vêm com reason 'reset').
            if (reason === 'input' && !props.disabled) {
              debouncedSearch(value)
            }
          }}
          onBlur={field.onBlur}
          getOptionSelected={(option, value) => option.name === value?.name}
          getOptionLabel={option => option.name}
          options={options}
          loading={loading}
          fullWidth={props.fullWidth}
          value={value}
          clearText="Limpar"
          closeText="Fechar"
          loadingText="Carregando..."
          noOptionsText="Sem opções"
          openText="Abrir"
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          renderInput={params => (
            <TextField
              {...params}
              error={meta.touched && Boolean(getError(meta.error))}
              helperText={meta.touched && getError(meta.error)}
              label={props.label}
              name={props.name}
              variant="outlined"
              placeholder={props.placeholder}
              className={classes.input}
              InputLabelProps={{
                ...props.InputLabelProps,
                disabled: props.disabled ? !props.isConsulting : false
              }}
              InputProps={{
                ...params.InputProps,
                disabled: props.disabled ? !props.isConsulting : false,
                endAdornment: (
                  <>
                    {open && loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        {initialLoading && (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}
      </>
    )
  }
